import { isValidLocale } from '@/i18n/config';
import { publicHostFlags } from '@/lib/config/network-env';

// `NETWORK_PREFERENCE` cookie — the user's chosen network.
//
// Set client-side when the user toggles in the UI. Read both server-side
// (in `getServerNetwork()` for SSR-aware fetches) and client-side (in
// the Zustand store on hydration).
//
// Cookie value is a `Network` literal — `'mainnet'` or `'devnet'`. Any
// other value is treated as missing and the deployment default kicks in.
// Max-age 1 year — preference should be persistent until the user
// explicitly flips it back.

import { isNetworkConfigured } from './config';
import { isNetwork, type Network } from './types';

export const NETWORK_PREFERENCE_COOKIE: string = 'NETWORK_PREFERENCE';
export const NETWORK_PREFERENCE_MAX_AGE: number = 365 * 24 * 60 * 60;

/** Parse a raw cookie string into a `Network` literal, or `null` when
 *  missing/invalid. Used by both the client store and the server helper.
 *  Accepts `local`, `devnet`, `mainnet`.
 *
 *  A preference naming a network THIS deployment cannot serve is treated as
 *  missing, so the caller falls back to `getDefaultNetwork()`. This matters
 *  because the cookie has a one-year max-age: deployed builds used to default
 *  every visitor to `local` (see `isNetworkConfigured`), and without this
 *  those stale cookies would keep them — and their analytics `network` tag —
 *  on `local` for up to a year after the default was corrected. */
export function parseNetworkPreference(raw: string | undefined | null): Network | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  if (!isNetwork(raw)) return null;
  return isNetworkConfigured(raw) ? raw : null;
}

// ─── Path-based demo-mode marker ──────────────────────────────────────
// When the user toggles demo-mode on, the URL gains a `/demo-mode/`
// prefix on every demo-relevant route. The path IS the canonical signal
// for what the page renders:
//   - `/markets/abc`            → mainnet content, indexable
//   - `/demo-mode/markets/abc`  → devnet content, noindex,nofollow
//
// Why path instead of query? Because `?demo-mode` collided with Next.js
// 15's build-time prerender on Vercel: the prerender shell was built
// once with empty searchParams, so every URL form (bare and `?demo-mode`)
// served the same wrong metadata. Path-based gives each URL form its
// own route file → its own prerender → correct metadata, no opt-outs.

/** The path segment that marks a URL as demo-mode. */
export const DEMO_MODE_SEGMENT: string = 'demo-mode';

/** First-segment paths that DO NOT get a `/demo-mode/` variant — they
 *  ALWAYS render bare. Static brand content, share-link surfaces,
 *  engineer-only routes, framework paths. NetworkLink leaves their
 *  hrefs alone; the catch-all dispatcher redirects `/demo-mode/<exception>`
 *  back to the bare equivalent.
 *
 *  Single source of truth — keep in sync with the route tree. */
export const DEMO_MODE_EXCEPTIONS: ReadonlySet<string> = new Set<string>([
  // Brand / content (network-independent, no demo variant needed)
  'about',
  'brand',
  'learn',
  'blog',
  'careers',
  'delete-account',
  'privacy',
  'terms',
  'risk',
  'compare',
  'support-mainnet',
  // Internal target for the pre-launch `/` rewrite. Middleware can execute
  // again after a rewrite, so this must remain outside demo-mode routing or a
  // fresh visitor loops between `/landing-home` and `/demo-mode/landing-home`.
  'landing-home',
  // Crawlable early-access invite landing — the URL X unfurls. Renders
  // bare so the OG meta is served directly (no noindex demo wrapper);
  // humans still hit the early-access gate via the app-shell overlay.
  'early-access',
  // Waitlist rewards platform (spec 004) — pre-launch, network-independent
  'rewards',
  // Engineer-only surfaces
  'admin',
  'debug',
  'live-playground',
  'simulate',
  // Auth-gated flows that have no demo variant — always render bare so
  // OAuth callbacks land on a stable URL and signup lands on a single
  // canonical onboarding flow regardless of network mode. /auth/callback
  // is registered with each OAuth provider as the bare URL — sending
  // devnet users through `/demo-mode/auth/callback` would 307 mid-OAuth
  // and flicker the URL bar while `code` is being exchanged.
  'auth',
  'creator',
  'onboarding',
  // One-shot share-link surfaces (already noindex via abuseSensitiveRobots)
  'gifts',
  'thesis',
  'trade',
  'proposal-back',
  'r',
  // Framework / API
  'api',
  '_next',
  '_vercel',
]);

/** First-segment paths whose content IS network-independent at the page
 *  level (a single rendered route handles both networks via the active
 *  SDK binding) BUT which the user expects to remain inside their
 *  current `/demo-mode/*` URL space — settings, onboarding, search, auth.
 *
 *  These are NOT in `DEMO_MODE_EXCEPTIONS`: NetworkLink prefixes their
 *  hrefs so demo-mode users don't drop out of the prefix on click. The
 *  middleware then transparently REWRITES `/demo-mode/<rewrite-path>/<x>`
 *  → `/<rewrite-path>/<x>` so the bare route file renders, while the
 *  user's URL bar stays at `/demo-mode/...`. The active SDK binding
 *  (driven by URL prefix) keeps fetching the right network.
 *
 *  Distinct from exceptions: exceptions REDIRECT (URL changes); rewrite
 *  paths REWRITE (URL preserved, different content). */
export const DEMO_MODE_REWRITE_PATHS: ReadonlySet<string> = new Set<string>([
  'live',
  'settings',
  'search',
  'workspace',
]);

/** Split a path-or-href into its pathname component and any trailing
 *  search/hash suffix. Lets the helpers below treat full hrefs (e.g.
 *  `/demo-mode?sort=createdAt`) the same as bare pathnames — important
 *  because router callsites pass full hrefs into `useNetworkAwarePath`
 *  / `<NetworkLink>`, and we don't want a `?` or `#` to defeat prefix
 *  detection. */
function splitPathAndSuffix(input: string): { path: string; suffix: string } {
  const queryIdx = input.indexOf('?');
  const hashIdx = input.indexOf('#');
  let cut = -1;
  if (queryIdx !== -1 && hashIdx !== -1) cut = Math.min(queryIdx, hashIdx);
  else if (queryIdx !== -1) cut = queryIdx;
  else if (hashIdx !== -1) cut = hashIdx;
  if (cut === -1) return { path: input, suffix: '' };
  return { path: input.slice(0, cut), suffix: input.slice(cut) };
}

/** Strip a leading `/{locale}` segment when present. Idempotent on bare
 *  paths. Preserves any trailing `?query` / `#hash`. Bookmarks and manual
 *  URLs sometimes include the locale even though `localePrefix: 'never'`
 *  keeps it out of canonical hrefs — demo-mode routing must tolerate both. */
export function stripLeadingLocalePath(pathname: string): string {
  const { path, suffix } = splitPathAndSuffix(pathname);
  const trimmed = path.replace(/^\/+/, '');
  if (trimmed.length === 0) return pathname;
  const [first, ...rest] = trimmed.split('/');
  if (first === undefined || !isValidLocale(first)) return pathname;
  const withoutLocale = rest.length > 0 ? `/${rest.join('/')}` : '/';
  return `${withoutLocale}${suffix}`;
}

/** True iff the pathname is the demo-mode root or any subpath under it.
 *  Tolerates a trailing `?query` / `#hash`. */
export function isDemoModePath(pathname: string): boolean {
  const { path } = splitPathAndSuffix(pathname);
  return path === `/${DEMO_MODE_SEGMENT}` || path.startsWith(`/${DEMO_MODE_SEGMENT}/`);
}

/** Prepend the demo-mode prefix to a bare pathname. Idempotent — calling
 *  on an already-prefixed path is a no-op. Preserves any trailing
 *  `?query` / `#hash`. */
export function addDemoModePath(pathname: string): string {
  if (isDemoModePath(pathname)) return pathname;
  const { path, suffix } = splitPathAndSuffix(pathname);
  if (path === '/' || path === '') return `/${DEMO_MODE_SEGMENT}${suffix}`;
  const prefixed = `/${DEMO_MODE_SEGMENT}${path.startsWith('/') ? path : `/${path}`}`;
  return `${prefixed}${suffix}`;
}

/** True iff a path is "demo-mode-aware" — i.e. it has a /demo-mode/
 *  variant and SHOULD be rewritten when the user is in demo mode.
 *
 *  False for: external URLs (http://, mailto:, hash-only), already-prefixed
 *  paths, and the exception list (about/learn/terms/auth/onboarding/...
 *  see DEMO_MODE_EXCEPTIONS — these have no demo equivalent).
 *  Tolerates a trailing `?query` / `#hash`. */
export function isDemoModeAwarePath(pathname: string): boolean {
  if (typeof pathname !== 'string' || pathname.length === 0) return false;
  if (!pathname.startsWith('/')) return false;
  if (isDemoModePath(pathname)) return false;
  const { path } = splitPathAndSuffix(stripLeadingLocalePath(pathname));
  const trimmed = path.replace(/^\/+/, '');
  if (trimmed.length === 0) {
    // Pre-launch the site root `/` is the bare marketing landing page — a
    // demo-mode exception (like `/early-access`) that must never be bounced
    // into `/demo-mode`. Once launched, `/` is the markets feed and is
    // demo-aware again. `NEXT_PUBLIC_EARLY_ACCESS_ENABLED` is a build-time
    // constant, so server and client agree without threading a param.
    return !publicHostFlags.earlyAccessEnabled;
  }
  const seg = trimmed.split('/')[0] ?? '';
  if (DEMO_MODE_EXCEPTIONS.has(seg)) return false;
  return true;
}

/** True iff the pathname is `/demo-mode/<rewrite-path>/...` — middleware
 *  uses this to rewrite the request to the bare route while preserving
 *  the URL. See `DEMO_MODE_REWRITE_PATHS`. Tolerates a trailing
 *  `?query` / `#hash`. */
export function isDemoModeRewritePath(pathname: string): boolean {
  if (typeof pathname !== 'string' || !isDemoModePath(pathname)) return false;
  const { path } = splitPathAndSuffix(stripLeadingLocalePath(stripDemoModePath(pathname)));
  const trimmed = path.replace(/^\/+/, '');
  if (trimmed.length === 0) return false;
  const seg = trimmed.split('/')[0] ?? '';
  return DEMO_MODE_REWRITE_PATHS.has(seg);
}

/** Strip the demo-mode prefix from a pathname. Idempotent — calling on
 *  a bare path is a no-op. Preserves any trailing `?query` / `#hash`. */
export function stripDemoModePath(pathname: string): string {
  const { path, suffix } = splitPathAndSuffix(pathname);
  if (path === `/${DEMO_MODE_SEGMENT}`) return `/${suffix}`;
  if (path.startsWith(`/${DEMO_MODE_SEGMENT}/`)) {
    return `${path.slice(`/${DEMO_MODE_SEGMENT}`.length)}${suffix}`;
  }
  return pathname;
}

/** True iff the user should be defaulted into demo-mode URLs. Used by:
 *   - Auth redirects (post-login/signup → /demo-mode/<intended-path>)
 *   - The "switch to demo mode" prompt modal
 *
 *  Encodes the policy: while mainnet trading is disabled, all non-mainnet
 *  users land in demo mode; once enabled, the user's explicit network
 *  preference wins (no forced redirect). Pass `mainnetEnabled` from the
 *  call site so server + client agree (don't read env in-line — the
 *  helper stays pure). */
export function shouldDefaultToDemoMode(network: Network, mainnetEnabled: boolean): boolean {
  if (network === 'mainnet') return false;
  return !mainnetEnabled;
}

/** Resolve the redirect URL after a user-initiated action (login,
 *  signup, onboarding completion) where we want the user to land on
 *  the right URL form for their runtime network. While mainnet trading
 *  is disabled, devnet users land at `/demo-mode/<path>`. Once enabled,
 *  every user lands on the bare path (mainnet-canonical). The `path`
 *  argument is the intended destination (e.g. `/markets`, `/onboarding`).
 *
 *  Exception paths (`/about`, `/onboarding`, `/auth/...`, etc.) NEVER
 *  receive the `/demo-mode/` prefix — they have no demo variant and
 *  always render at the bare URL. Adding the prefix would either 404 or
 *  bounce through the demo catch-all's redirect-to-bare branch — both
 *  bad UX. Stripping the prefix on the way back is still safe. */
export function resolveAuthRedirect(
  path: string,
  network: Network,
  mainnetEnabled: boolean,
): string {
  const barePath = stripLeadingLocalePath(path);
  if (shouldDefaultToDemoMode(network, mainnetEnabled) && isDemoModeAwarePath(barePath)) {
    return addDemoModePath(barePath);
  }
  return stripDemoModePath(barePath);
}

/** Mutate `url.pathname` to reflect the user's runtime network.
 *  Non-mainnet networks add the `/demo-mode/` prefix; mainnet strips it.
 *  Used by the network-toggle UI and share-URL builders. Returns the
 *  same URL for chaining. */
export function withDemoMode(url: URL, network: Network): URL {
  if (network === 'mainnet') {
    url.pathname = stripDemoModePath(url.pathname);
  } else {
    url.pathname = addDemoModePath(stripLeadingLocalePath(url.pathname));
  }
  return url;
}

// ─── OG image URL: demo-mode query (separate concern) ────────────────
// OG image route handlers (`/api/og/...`) are dynamic per-request and
// read demo-mode from their OWN query string (`?demo-mode`) — separate
// from the page URL's path-prefix scheme. These two helpers exist for
// that narrow purpose only:
//   - `appendDemoModeFlag(url)` — used by `getOgImageUrl({ demoMode })`
//     to mark an OG image URL as devnet (renders [DEMO MODE] badge).
//   - `isDemoModeUrlSearchParams(searchParams)` — used by OG route
//     handlers to check their own query string.
// PAGE URLs do NOT use these; pages use the path-based helpers above.

/** Internal — append `?demo-mode` (as a flag, no `=`) to an OG-image URL.
 *  Idempotent. Used only by the OG image URL builder; page URLs use
 *  `withDemoMode(url, network)` (path-based) instead. */
export function appendDemoModeFlag(url: URL): void {
  if (url.searchParams.has('demo-mode')) return;
  const sep = url.search.length === 0 ? '?' : '&';
  url.search = `${url.search}${sep}demo-mode`;
}

/** Detect a demo-mode flag on a `URLSearchParams` — used by OG route
 *  handlers reading their own URL's query string. Page-level demo-mode
 *  detection lives in path helpers (`isDemoModePath`). */
export function isDemoModeUrlSearchParams(searchParams: URLSearchParams): boolean {
  return searchParams.has('demo-mode');
}
